import { useCallback, useEffect, useRef, useState } from 'react';
import { db } from '../utils/db';
import { pendingProposals } from '../utils/dbSelectors';
import { interpret } from '../utils/intents';
import { playSound } from '../utils/sound';
import { canPlaySound, isQuietMode, resolveAvatarState, speechVolume } from '../utils/houseRules';
import { useDb } from './useDb';
import { useAgentSim } from './useAgentSim';
import { useTimers } from './useTimers';
import { useSpokenLine } from './useSpokenLine';
import { useSpecSettings } from '../contexts/SpecSettingsContext';
import type { AgentId, AvatarState, HouseMode, Processing, SoundId } from '../types/anna';
import { applyStageEffect } from '../utils/stageEffects';
import type { Activity, CardSpawn, Daypart, StageCard, StageView } from '../types/stage';

const WORD_MS = 330;
const MAX_CARDS = 3;

/** What the wall looks like in each house mode, wherever the mode was changed (wall, phone, desk). */
const MODE_LOOK: Record<HouseMode, { base: AvatarState; view: StageView; daypart?: Daypart; quietLine?: string; clearCards: boolean }> = {
  Vrij: { base: 'rust', view: 'stage', clearCards: false },
  Focus: { base: 'nietStoren', view: 'stage', daypart: 'focus', quietLine: 'Focus · ik meld alleen wat dringend is', clearCards: true },
  Samen: { base: 'rust', view: 'stage', quietLine: 'Samen · ik toon niets persoonlijks', clearCards: true },
  Slapen: { base: 'slaapt', view: 'sleep', daypart: 'nacht', clearCards: true },
  Afwezig: { base: 'rust', view: 'stage', quietLine: 'Afwezig · ik bewaak het huis', clearCards: true },
};

/** The wall's stage director: scenario moments, Anna's speech, summoned cards and approvals. Data lives in the shared db. */
export function useAnnaStage() {
  const { soundOn } = useSpecSettings();
  const soundRef = useRef(soundOn);
  soundRef.current = soundOn;

  const moments = useDb((t) => t.moments);
  const meta = useDb((t) => t.meta);
  const pending = useDb(pendingProposals);
  const roomIds = useDb((t) => t.rooms.map((r) => r.id));

  const first = moments[0];
  const [momentIndex, setMomentIndex] = useState(0);
  const [daypart, setDaypart] = useState<Daypart>(first.daypart);
  const [base, setBase] = useState<AvatarState>(first.base);
  const [view, setView] = useState<StageView>(first.view);
  const [quietLine, setQuietLine] = useState<string | undefined>(first.quietLine);
  const [activity, setActivity] = useState<Activity>('idle');
  const [thinkingWhere, setThinkingWhere] = useState<Processing>('lokaal');
  const [caption, setCaption] = useState<{ id: number; text: string } | null>(null);
  const [revealed, setRevealed] = useState(0);
  const [cards, setCards] = useState<StageCard[]>([]);
  const [outcomes, setOutcomes] = useState<Record<string, 'ok' | 'nee'>>({});
  const [pulseKey, setPulseKey] = useState(0);
  const [flash, setFlash] = useState<{ agent: AgentId; id: number } | null>(null);
  const [toast, setToast] = useState<{ id: number; text: string; tone: SoundId } | null>(null);
  const [transient, setTransient] = useState<AvatarState | null>(null);
  /** Bumps when the demo data resets, so the agent simulation restarts with it */
  const [epoch, setEpoch] = useState(0);

  const { mode, stopped } = meta;
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  const viewRef = useRef(view);
  viewRef.current = view;
  /** The mode the wall last dressed itself for; anything else came from another screen */
  const dressedFor = useRef<HouseMode>(mode);

  const { later, clearTimers } = useTimers();
  const voiceOn = useCallback(() => soundRef.current, []);
  const spoken = useSpokenLine(later, voiceOn);
  const uid = useRef(0);

  const sound = useCallback((id: SoundId, safety = false) => {
    if (soundRef.current && canPlaySound(safety)) playSound(id);
  }, []);

  const showToast = useCallback((text: string, tone: SoundId) => setToast({ id: Date.now(), text, tone }), []);

  const addCard = useCallback((spawn: CardSpawn) => {
    setCards((prev) => {
      const without = prev.filter((c) => c.kind !== spawn.kind);
      const left = without.filter((c) => c.side === 'links').length;
      const side = spawn.side ?? (left <= without.length - left ? 'links' : 'rechts');
      return [...without, { uid: `c${uid.current++}`, kind: spawn.kind, side, payload: spawn.payload }].slice(-MAX_CARDS);
    });
  }, []);

  const removeCard = useCallback((cardUid: string) => setCards((prev) => prev.filter((c) => c.uid !== cardUid)), []);

  const dressFor = useCallback((m: HouseMode) => {
    const look = MODE_LOOK[m];
    dressedFor.current = m;
    setBase(look.base);
    setView(look.view);
    setQuietLine(look.quietLine);
    setDaypart((d) => look.daypart ?? (d === 'focus' || d === 'nacht' || d === 'alarm' ? 'middag' : d));
    if (look.clearCards) setCards([]);
  }, []);

  const enterMode = useCallback(
    (m: HouseMode) => {
      dressFor(m);
      db.setMode(m, 'wand');
    },
    [dressFor],
  );

  // Another screen changed the mode (phone, desk): the wall follows.
  useEffect(() => {
    if (mode !== dressedFor.current) dressFor(mode);
  }, [mode, dressFor]);

  const speakLine = useCallback(
    (text: string, spawns: CardSpawn[] = [], volume = 1) => {
      const id = Date.now();
      setCaption({ id, text });
      setRevealed(0);
      setActivity('speaking');
      spawns.forEach((s) => later(() => addCard(s), (s.atWord ?? 0) * WORD_MS));
      spoken.speak(text, {
        wordMs: WORD_MS,
        volume: Math.min(volume, speechVolume()),
        onWord: setRevealed,
        onDone: () => {
          setActivity((a) => (a === 'speaking' ? 'idle' : a));
          // Captions fade 6 s after the last word.
          later(() => setCaption((c) => (c && c.id === id ? null : c)), 6000);
        },
      });
    },
    [addCard, later, spoken],
  );

  /** Owner interrupts: Anna stops mid-sentence immediately. */
  const bargeIn = useCallback(() => {
    spoken.cancel();
    clearTimers();
    setCaption(null);
    setActivity('idle');
  }, [clearTimers, spoken]);

  const goToMoment = useCallback(
    (index: number) => {
      const m = moments[index];
      bargeIn();
      setMomentIndex(index);
      dressedFor.current = m.mode;
      if (m.id === 'rust') {
        db.reset();
        setEpoch((e) => e + 1);
      }
      db.setMeta({ time: m.time, mode: m.mode });
      setDaypart(m.daypart);
      setBase(m.base);
      setView(m.view);
      setQuietLine(m.quietLine);
      setTransient(null);
      if (!m.keepCards) setCards([]);

      // Keep the shared data in step with the scenario.
      if (m.id === 'ochtend') db.setDevice('koffie', 'aan');
      if (m.id === 'deur') {
        db.log({ agent: 'huis', summary: 'Bezorger aan de deur, stil gemeld', level: 'zelf', where: 'lokaal', outcome: 'gemeld', traceId: 'deur' });
      }
      if (m.view === 'approvals') {
        db.resetProposals();
        setOutcomes({});
      }
      if (m.view === 'alarm') {
        db.setDevice('water-keuken', 'nat');
        db.log({ agent: 'huis', summary: 'Water in de keuken gemeld', level: 'zelf', where: 'lokaal', outcome: 'gemeld', traceId: 'waterlek' });
      }

      const quiet = isQuietMode(m.mode);
      const start = m.keepCards ? 150 : 520;
      if (m.pulse) later(() => setPulseKey((k) => k + 1), start);
      if (m.chime) {
        later(() => {
          setPulseKey((k) => k + 1);
          sound('meldt');
        }, start);
      }
      if (m.view === 'alarm') later(() => sound('meldt', true), start);
      const speakAt = start + (m.chime ? 900 : 0);
      if (m.speech) {
        later(() => speakLine(m.speech as string, m.cards ?? [], quiet && m.view !== 'alarm' ? 0.5 : 1), speakAt);
      } else {
        (m.cards ?? []).forEach((c, i) => later(() => addCard(c), start + 120 * i));
      }
    },
    [addCard, bargeIn, later, moments, sound, speakLine],
  );

  const next = useCallback(() => goToMoment((momentIndex + 1) % moments.length), [goToMoment, momentIndex, moments.length]);
  const prev = useCallback(
    () => goToMoment((momentIndex - 1 + moments.length) % moments.length),
    [goToMoment, momentIndex, moments.length],
  );

  const openApprovals = useCallback(() => {
    bargeIn();
    setCards([]);
    setOutcomes({});
    setView('approvals');
  }, [bargeIn]);

  const leaveApprovals = useCallback(() => setView('stage'), []);

  const settle = useCallback(
    (id: string, outcome: 'ok' | 'nee') => {
      const p = pendingRef.current.find((x) => x.id === id);
      if (!p) return;
      const remaining = pendingRef.current.length - 1;
      // Outcome first, so the card knows which way to exit; the db write follows a tick later.
      setOutcomes((o) => ({ ...o, [id]: outcome }));
      if (outcome === 'ok') {
        window.setTimeout(() => db.approveProposal(id), 0);
        sound('klaar');
        setFlash({ agent: p.agent, id: Date.now() });
        // The card flies into Anna; her thread for that agent lights up.
        setTransient('werkt');
        later(() => setTransient(null), 2400);
      } else {
        window.setTimeout(() => db.rejectProposal(id), 0);
        showToast('Afgewezen. Anna leert hiervan.', 'geweigerd');
      }
      if (remaining <= 0) {
        later(() => {
          setView('stage');
          speakLine('Alles afgehandeld.');
        }, 900);
      }
    },
    [later, showToast, sound, speakLine],
  );

  const respond = useCallback(
    (text: string) => {
      const res = interpret(text, { mode: db.getState().meta.mode, waiting: pendingRef.current.length, roomIds });
      if (res.action === 'ochtend') {
        goToMoment(1);
        return;
      }
      setActivity('thinking');
      setThinkingWhere(res.where);
      later(() => {
        if (res.tone) sound(res.tone);
        if (res.concerned) {
          setTransient('bezorgd');
          later(() => setTransient(null), 4500);
        }
        applyStageEffect(res.effect);
        switch (res.action) {
          case 'approvals':
            setCards([]);
            setOutcomes({});
            setView('approvals');
            break;
          case 'focus':
            enterMode('Focus');
            break;
          case 'slapen':
            enterMode('Slapen');
            break;
          case 'vrij':
            enterMode('Vrij');
            break;
          case 'werkt':
            setTransient('werkt');
            later(() => setTransient(null), 9000);
            break;
          default:
            if (viewRef.current === 'sleep' && res.cards?.length) setView('stage');
        }
        if (res.toast) showToast(res.toast, res.tone ?? 'klaar');
        speakLine(res.say, res.cards ?? [], isQuietMode(db.getState().meta.mode) ? 0.5 : 1);
      }, 700);
    },
    [enterMode, goToMoment, later, roomIds, showToast, sound, speakLine],
  );

  const setListening = useCallback((on: boolean) => {
    setActivity((a) => (on ? 'listening' : a === 'listening' ? 'idle' : a));
  }, []);

  // Noodstop is house-wide: whichever screen pulled it, the wall falls silent too.
  useEffect(() => {
    if (!stopped) return;
    bargeIn();
    setCards((prevCards) => prevCards.filter((c) => c.kind !== 'verwarming'));
  }, [stopped, bargeIn]);

  const emergencyStop = useCallback(() => db.stop('wand'), []);
  const resume = useCallback(() => db.resume('wand'), []);

  const ackAlarm = useCallback(() => {
    bargeIn();
    db.setDevice('water-keuken', 'gezien');
    setView('sleep');
    setBase('slaapt');
    setDaypart('nacht');
  }, [bargeIn]);

  const notifyApproved = useCallback(
    (agent: AgentId) => {
      sound('klaar');
      setFlash({ agent, id: Date.now() });
    },
    [sound],
  );

  // The wall opens on the first moment, whatever another screen did to the shared data.
  useEffect(() => {
    dressedFor.current = first.mode;
    db.setMeta({ time: first.time, mode: first.mode });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** Studio finishes in the background: its satellite flashes, a short rising tone. */
  const finishRender = useCallback(() => {
    const r = db.getState().renders[0];
    if (!r || r.status === 'klaar') return;
    db.finishRender(r.id);
    setFlash({ agent: 'studio', id: Date.now() });
    sound('klaar');
  }, [sound]);

  useAgentSim(epoch, finishRender);

  const avatarState = resolveAvatarState({ stopped, activity, transient, rest: base });

  return {
    moment: moments[momentIndex],
    momentIndex,
    momentCount: moments.length,
    mode,
    daypart,
    view,
    quietLine,
    activity,
    thinkingWhere,
    caption,
    revealed,
    cards,
    pending,
    outcomes,
    pulseKey,
    flash,
    toast,
    stopped,
    avatarState,
    quiet: isQuietMode(mode),
    next,
    prev,
    goToMoment,
    respond,
    bargeIn,
    setListening,
    openApprovals,
    leaveApprovals,
    approve: (id: string) => settle(id, 'ok'),
    reject: (id: string) => settle(id, 'nee'),
    removeCard,
    showToast,
    dismissToast: () => setToast(null),
    emergencyStop,
    resume,
    ackAlarm,
    notifyApproved,
    sound,
  };
}

export type AnnaStage = ReturnType<typeof useAnnaStage>;
